import { memo } from "react";
import { useTranslation } from "react-i18next";
import { usePlayerStore, getPlayerActions } from "@/lib/player/player.store";
import type { TranscriptLine } from "./hooks/useTranscript.hook";

interface TranscriptLineRowProps {
	line: TranscriptLine;
	isActive: boolean;
	isDualLanguage: boolean;
	isTranslating: boolean;
	lineRef?: React.RefObject<HTMLDivElement | null>;
}

export const TranscriptLineRow = memo(function TranscriptLineRow({
	line,
	isActive,
	isDualLanguage,
	isTranslating,
	lineRef,
}: TranscriptLineRowProps) {
	const { t } = useTranslation();
	const formatTime = usePlayerStore((s) => s.formatTime);
	const isSp1 = /1/i.test(line.speaker);
	const seekSec = line.globalStartMs / 1000;

	const activeSpeakerColor = isSp1
		? "text-orange-300 font-medium"
		: "text-blue-300 font-medium";

	return (
		<div
			ref={lineRef}
			className={[
				"rounded-xl px-3 py-1.5 transition-all duration-150 cursor-pointer select-none active:opacity-70",
				isActive ? "bg-white/10" : "hover:bg-white/5",
			].join(" ")}
			onClick={() => getPlayerActions().seek(seekSec)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ")
					getPlayerActions().seek(seekSec);
			}}
			role="button"
			tabIndex={0}
			aria-label={t("transcript.seekTo", { time: formatTime(seekSec) })}
		>
			{isDualLanguage ? (
				<div className="space-y-1">
					<p
						className={[
							"text-sm leading-snug",
							isActive ? activeSpeakerColor : "text-gray-300",
						].join(" ")}
					>
						{line.text}
					</p>
					<p
						className={[
							"text-xs leading-snug italic border-l-2 border-white/10 pl-2",
							isActive ? "text-gray-400 font-medium" : "text-gray-600",
						].join(" ")}
					>
						{line.translationText ??
							(isTranslating ? (
								<span className="opacity-40">
									{t("transcript.translation.notTranslated")}
								</span>
							) : null)}
					</p>
				</div>
			) : (
				<p
					className={[
						"text-sm leading-snug",
						isActive ? activeSpeakerColor : "text-gray-500",
					].join(" ")}
				>
					{line.text}
				</p>
			)}
		</div>
	);
});
